import axios from 'axios';
import { Room, Booking, Order, RestaurantOrder, CreateOrderRequest, UpdateOrderStatusRequest, UpdatePaymentStatusRequest, BanquetHall, BanquetBooking, BanquetOrder } from '../types';
import { mockBanquetHalls, mockBanquetBookings, mockBanquetOrders, getBookingsForHall, getOrdersForBooking, getHallsWithBookings } from '../data/banquetData';

// Mock data storage for room bookings (temporary storage that persists until browser refresh)
let mockRoomBookings: Booking[] = [];

const API_BASE_URL = 'https://snowtable.in/voicedemo2';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);

export const apiService = {
  // Get rooms with optional date filter
  getRooms: async (date?: string): Promise<Room[]> => {
    try {
      const url = date ? `/api/rooms?date=${date}` : '/api/rooms';
      const response = await api.get(url);
      return response.data.map((room: any) => ({
        roomId: room.room_id?.toString() || '',
        guestName: room.guest_name,
        bookingStatus: room.booking_status,
        roomStatus: room.room_status || 'available',
        roomType: room.room_type || 'Standard',
        stayStartDate: room.stay_start_date,
        stayEndDate: room.stay_end_date,
      }));
    } catch (error) {
      console.error('Error fetching rooms:', error);
      throw new Error('Failed to load rooms');
    }
  },

  // Get all bookings
  getAllBookings: async (): Promise<Booking[]> => {
    try {
      const response = await api.get('/api/bookings');
      const backendBookings = response.data.map((booking: any) => ({
        bookingId: booking.booking_id,
        roomId: booking.room_id,
        guestId: booking.guest_id,
        guestName: booking.guest_name,
        phone: booking.phone,
        email: booking.email,
        idProof: booking.id_proof,
        bookingDate: booking.booking_date,
        originalCheckinDate: booking.original_checkin_date,
        originalCheckoutDate: booking.original_checkout_date,
        actualCheckinDate: booking.actual_checkin_date,
        actualCheckoutDate: booking.actual_checkout_date,
        currentCheckoutDate: booking.current_checkout_date,
        extensionCount: booking.extension_count,
        lastExtendedDate: booking.last_extended_date,
        bookingStatus: booking.booking_status,
        totalAmount: parseFloat(booking.total_amount || '0'),
        roomType: booking.room_type,
        roomStatus: booking.room_status,
        bookingSource: booking.booking_source,
      }));
      
      // Return union of backend data and mock data
      return [...backendBookings, ...mockRoomBookings];
    } catch (error) {
      console.error('Error fetching bookings:', error);
      // If backend fails, return only mock data
      return mockRoomBookings;
    }
  },

  // Get orders for a specific booking
  getOrders: async (bookingId: string): Promise<Order[]> => {
    try {
      const response = await api.get(`/api/orders/${bookingId}`);
      return response.data.map((order: any) => ({
        orderId: order.order_id,
        bookingId: order.booking_id,
        itemName: order.item_name,
        orderTime: order.order_time,
        quantity: order.quantity,
        status: order.status,
        totalAmount: order.total_amount,
        unitPrice: order.unit_price,
      }));
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return []; // No orders found
      }
      console.error('Error fetching orders:', error);
      throw new Error('Failed to load orders');
    }
  },

  // Get bookings for a specific room
  getBookings: async (roomId: string): Promise<Booking[]> => {
    try {
      const response = await api.get(`/api/bookings/${roomId}`);
      return response.data.map((booking: any) => ({
        bookingId: booking.booking_id,
        roomId: booking.room_id,
        guestId: booking.guest_id,
        guestName: booking.guest_name,
        phone: booking.phone,
        email: booking.email,
        idProof: booking.id_proof,
        bookingDate: booking.booking_date,
        originalCheckinDate: booking.original_checkin_date,
        originalCheckoutDate: booking.original_checkout_date,
        actualCheckinDate: booking.actual_checkin_date,
        actualCheckoutDate: booking.actual_checkout_date,
        currentCheckoutDate: booking.current_checkout_date,
        extensionCount: booking.extension_count,
        lastExtendedDate: booking.last_extended_date,
        bookingStatus: booking.booking_status,
        totalAmount: parseFloat(booking.total_amount || '0'),
        roomType: booking.room_type,
        roomStatus: booking.room_status,
        bookingSource: booking.booking_source,
      }));
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return []; // No bookings found
      }
      console.error('Error fetching bookings:', error);
      throw new Error('Failed to load bookings');
    }
  },

  // Health check
  healthCheck: async (): Promise<any> => {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw new Error('Health check failed');
    }
  },

  // Restaurant Order Management APIs
  // Create a new restaurant order
  createRestaurantOrder: async (orderData: CreateOrderRequest): Promise<RestaurantOrder> => {
    try {
      const response = await api.post('/api/restaurant/orders', orderData);
      return response.data;
    } catch (error) {
      console.error('Error creating restaurant order:', error);
      throw new Error('Failed to create order');
    }
  },

  // Get a specific restaurant order
  getRestaurantOrder: async (orderId: number): Promise<RestaurantOrder> => {
    try {
      const response = await api.get(`/api/restaurant/orders/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching restaurant order:', error);
      throw new Error('Failed to load order');
    }
  },

  // List restaurant orders with optional status filtering
  getRestaurantOrders: async (status?: string): Promise<RestaurantOrder[]> => {
    try {
      const url = status ? `/api/restaurant/orders?status=${status}` : '/api/restaurant/orders';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching restaurant orders:', error);
      throw new Error('Failed to load orders');
    }
  },

  // Add items to existing order
  addItemsToOrder: async (orderId: number, items: any[]): Promise<RestaurantOrder> => {
    try {
      const response = await api.post(`/api/restaurant/orders/${orderId}/items`, { items });
      return response.data;
    } catch (error) {
      console.error('Error adding items to order:', error);
      throw new Error('Failed to add items to order');
    }
  },

  // Update order status
  updateOrderStatus: async (orderId: number, statusData: UpdateOrderStatusRequest): Promise<RestaurantOrder> => {
    try {
      const response = await api.put(`/api/restaurant/orders/${orderId}/status`, statusData);
      return response.data;
    } catch (error) {
      console.error('Error updating order status:', error);
      throw new Error('Failed to update order status');
    }
  },

  // Update payment status
  updatePaymentStatus: async (orderId: number, paymentData: UpdatePaymentStatusRequest): Promise<RestaurantOrder> => {
    try {
      const response = await api.put(`/api/restaurant/orders/${orderId}/payment`, paymentData);
      return response.data;
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw new Error('Failed to update payment status');
    }
  },

  // Find orders by customer name
  getOrdersByCustomer: async (customerName: string): Promise<RestaurantOrder[]> => {
    try {
      const response = await api.get(`/api/restaurant/orders/customer/${encodeURIComponent(customerName)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching orders by customer:', error);
      throw new Error('Failed to load customer orders');
    }
  },

  // AI Command Processing
  processCommand: async (text: string): Promise<any> => {
    try {
      const response = await api.post('/api/command/text', { text });
      return response.data;
    } catch (error) {
      console.error('Error processing command:', error);
      throw new Error('Failed to process command');
    }
  },

  // Create a new booking
  createBooking: async (bookingData: {
    roomId: string;
    guestName: string;
    phone: string;
    email?: string;
    idProof?: string;
    checkinDate: string;
    checkoutDate: string;
    totalAmount: number;
    roomType: string;
    bookingSource: string;
  }): Promise<Booking> => {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Create new booking with mock data
      const newBooking: Booking = {
        bookingId: Math.max(...mockRoomBookings.map(b => b.bookingId), 10000) + 1,
        roomId: bookingData.roomId,
        guestId: Math.max(...mockRoomBookings.map(b => b.guestId), 20000) + 1,
        guestName: bookingData.guestName,
        phone: bookingData.phone,
        email: bookingData.email,
        idProof: bookingData.idProof,
        bookingDate: new Date().toISOString().split('T')[0],
        originalCheckinDate: bookingData.checkinDate,
        originalCheckoutDate: bookingData.checkoutDate,
        actualCheckinDate: bookingData.checkinDate,
        actualCheckoutDate: bookingData.checkoutDate,
        currentCheckoutDate: bookingData.checkoutDate,
        extensionCount: 0,
        bookingStatus: 'confirmed',
        totalAmount: bookingData.totalAmount,
        roomType: bookingData.roomType,
        roomStatus: 'occupied',
        bookingSource: bookingData.bookingSource,
      };
      
      // Add to mock data (temporary storage until browser refresh)
      mockRoomBookings.push(newBooking);
      
      return newBooking;
    } catch (error) {
      console.error('Error creating booking:', error);
      throw new Error('Failed to create booking');
    }
  },

  // Banquet Management APIs
  // Get banquet halls with optional date filter
  getBanquetHalls: async (date?: string): Promise<BanquetHall[]> => {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Return mock data with booking information
      return getHallsWithBookings();
    } catch (error) {
      console.error('Error fetching banquet halls:', error);
      throw new Error('Failed to load banquet halls');
    }
  },

  // Get all banquet bookings
  getAllBanquetBookings: async (): Promise<BanquetBooking[]> => {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Return mock data
      return mockBanquetBookings;
    } catch (error) {
      console.error('Error fetching banquet bookings:', error);
      throw new Error('Failed to load banquet bookings');
    }
  },

  // Get orders for a specific banquet booking
  getBanquetOrders: async (bookingId: string): Promise<BanquetOrder[]> => {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Return mock data for the specific booking
      return getOrdersForBooking(parseInt(bookingId));
    } catch (error) {
      console.error('Error fetching banquet orders:', error);
      throw new Error('Failed to load banquet orders');
    }
  },

  // Get banquet bookings for a specific hall
  getBanquetBookings: async (hallId: string): Promise<BanquetBooking[]> => {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Return mock data for the specific hall
      return getBookingsForHall(hallId);
    } catch (error) {
      console.error('Error fetching banquet bookings:', error);
      throw new Error('Failed to load banquet bookings');
    }
  },

  // Create a new banquet booking
  createBanquetBooking: async (bookingData: {
    hallId: string;
    guestName: string;
    phone: string;
    email?: string;
    eventName: string;
    eventType: BanquetBooking['eventType'];
    eventDate: string;
    startTime: string;
    endTime: string;
    guestCount: number;
    totalAmount: number;
    advanceAmount: number;
    specialRequirements?: string;
    cateringRequired: boolean;
    decorationRequired: boolean;
    audioVisualRequired: boolean;
    bookingSource: string;
  }): Promise<BanquetBooking> => {
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Create new booking with mock data
      const newBooking: BanquetBooking = {
        bookingId: Math.max(...mockBanquetBookings.map(b => b.bookingId)) + 1,
        hallId: bookingData.hallId,
        guestId: Math.max(...mockBanquetBookings.map(b => b.guestId)) + 1,
        guestName: bookingData.guestName,
        phone: bookingData.phone,
        email: bookingData.email,
        eventName: bookingData.eventName,
        eventType: bookingData.eventType,
        bookingDate: new Date().toISOString().split('T')[0],
        eventDate: bookingData.eventDate,
        startTime: bookingData.startTime,
        endTime: bookingData.endTime,
        duration: Math.max(1, (new Date(`2000-01-01T${bookingData.endTime}`).getTime() - new Date(`2000-01-01T${bookingData.startTime}`).getTime()) / (1000 * 60 * 60)),
        guestCount: bookingData.guestCount,
        totalAmount: bookingData.totalAmount,
        advanceAmount: bookingData.advanceAmount,
        balanceAmount: bookingData.totalAmount - bookingData.advanceAmount,
        bookingStatus: 'confirmed',
        specialRequirements: bookingData.specialRequirements,
        cateringRequired: bookingData.cateringRequired,
        decorationRequired: bookingData.decorationRequired,
        audioVisualRequired: bookingData.audioVisualRequired,
        bookingSource: bookingData.bookingSource,
        createdBy: 'admin',
        lastModified: new Date().toISOString(),
      };
      
      // Add to mock data (in a real app, this would be handled by the backend)
      mockBanquetBookings.push(newBooking);
      
      return newBooking;
    } catch (error) {
      console.error('Error creating banquet booking:', error);
      throw new Error('Failed to create banquet booking');
    }
  },
};

export default apiService;

